import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { IconSend, IconX } from "@tabler/icons-react";
import { relayLaunch, type ReceivedLaunch } from "../api";

/**
 * Corpo começando em dígito, dígitos ou letras maiúsculas depois: cobre o formato novo
 * (`IMB-48217K`) e o legado sequencial (`IMB-00231`), que segue impresso em placa.
 */
const IMB = /^IMB-?[0-9][0-9A-Z]{0,11}$/i;

/**
 * Repasse do lançamento ao quadro da imobiliária (hub#753).
 *
 * A imobiliária **escolhe quem** recebe. Repassar ao time inteiro por padrão faria o
 * corretor que não atua naquele produto receber material irrelevante — e material
 * irrelevante é o que faz o corretor parar de abrir o que a imobiliária manda.
 *
 * Os corretores são informados pelo **código IMB**, e não escolhidos de uma lista do
 * roster: o roster do agency guarda o id canônico do corretor, não o código público,
 * e é o código que o `launches-api` usa para resolver quem é quem. Selecionar da
 * lista depende de o roster passar a expor o IMB — follow-up registrado na PR.
 */
export function RelayLaunchModal({
  launch,
  onClose,
}: {
  launch: ReceivedLaunch;
  onClose: () => void;
}) {
  const queryClient = useQueryClient();
  const [codes, setCodes] = useState<string[]>([]);
  const [input, setInput] = useState("");
  const [missing, setMissing] = useState(false);

  const relay = useMutation({
    mutationFn: (all: string[]) => relayLaunch(launch.launchId, all),
    onSuccess: (result) => {
      if (result.relayed > 0) {
        toast.success(
          result.relayed === 1
            ? "Lançamento repassado para 1 corretor."
            : `Lançamento repassado para ${result.relayed} corretores.`,
        );
      }

      // Explica a diferença entre o que ela escolheu e o que foi enviado, em vez de
      // deixá-la contar na mão.
      if (result.skipped > 0) {
        toast.warning(
          result.skipped === 1
            ? "1 corretor não recebeu: já tinha o convite ou desativou o módulo Lançamentos."
            : `${result.skipped} corretores não receberam: já tinham o convite ou desativaram o módulo Lançamentos.`,
        );
      }

      if (result.unresolved.length > 0) {
        toast.warning(`Não encontramos: ${result.unresolved.join(", ")}. Confira o código.`);
      }

      void queryClient.invalidateQueries({ queryKey: ["agency-launches"] });
      onClose();
    },
    onError: () => toast.error("Não foi possível repassar o lançamento."),
  });

  function addCode(raw: string) {
    const value = raw.trim();
    if (!value) return;

    if (!IMB.test(value)) {
      toast.error(`Código inválido: "${value}". Use o formato IMB-00231.`);
      return;
    }

    const normalized = value.toUpperCase();
    setCodes((prev) => (prev.includes(normalized) ? prev : [...prev, normalized]));
    setInput("");
    setMissing(false);
  }

  function onKeyDown(event: React.KeyboardEvent<HTMLInputElement>) {
    if (event.key === "Enter" || event.key === ",") {
      event.preventDefault();
      addCode(input);
    } else if (event.key === "Backspace" && !input && codes.length > 0) {
      setCodes((prev) => prev.slice(0, -1));
    }
  }

  function submit() {
    // Inclui o código ainda digitado: esquecer de confirmar o último é o engano mais
    // comum, e perder o repasse por causa disso seria gratuito.
    const pending = input.trim();
    const all = pending && IMB.test(pending) ? [...codes, pending.toUpperCase()] : codes;

    if (all.length === 0) {
      setMissing(true);
      document.getElementById("relay-code")?.focus();
      toast.error("Informe ao menos um código IMB.");
      return;
    }

    relay.mutate(all);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-start justify-center overflow-y-auto bg-slate-900/40 p-4 backdrop-blur-sm sm:items-center">
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Repassar ${launch.launchName}`}
        className="relative my-8 w-full max-w-lg rounded-2xl border border-slate-200 bg-white shadow-xl"
      >
        <div className="flex items-start justify-between gap-3 border-b border-slate-100 px-5 py-3.5">
          <div>
            <h2 className="text-[15px] font-bold tracking-tight">Repassar ao time</h2>
            <p className="mt-0.5 text-[12px] text-slate-500">
              {launch.launchName} · {launch.developerName}
            </p>
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Fechar"
            className="flex h-8 w-8 items-center justify-center rounded-lg text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-700"
          >
            <IconX size={16} />
          </button>
        </div>

        <div className="px-5 py-4">
          <label htmlFor="relay-code" className="mb-1 block text-[12px] font-medium text-slate-600">
            Códigos IMB dos corretores
          </label>
          <div
            className={
              "flex flex-wrap items-center gap-1.5 rounded-lg border bg-white px-2 py-1.5 " +
              (missing ? "border-rose-400" : "border-slate-200")
            }
          >
            {codes.map((code) => (
              <span
                key={code}
                className="inline-flex items-center gap-1 rounded-md bg-slate-100 px-1.5 py-0.5 text-[12px] font-medium text-slate-700"
              >
                {code}
                <button
                  type="button"
                  aria-label={`Remover ${code}`}
                  onClick={() => setCodes((prev) => prev.filter((c) => c !== code))}
                  className="text-slate-400 hover:text-slate-700"
                >
                  <IconX size={12} />
                </button>
              </span>
            ))}
            <input
              id="relay-code"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyDown={onKeyDown}
              onBlur={() => input.trim() && addCode(input)}
              aria-invalid={missing}
              placeholder={codes.length > 0 ? "Adicionar outro…" : "Ex.: IMB-00231"}
              className="min-w-[170px] flex-1 bg-transparent text-[13px] outline-none placeholder:text-slate-400"
            />
          </div>
          <p className={"mt-1.5 text-[11.5px] " + (missing ? "text-rose-600" : "text-slate-500")}>
            {missing
              ? "Informe ao menos um código IMB."
              : "Enter ou vírgula para adicionar vários. Cada corretor recebe o convite dele e decide."}
          </p>
        </div>

        <div className="flex justify-end gap-2 border-t border-slate-100 px-5 py-3">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-[13px] font-medium text-slate-700 hover:bg-slate-50"
          >
            Cancelar
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={relay.isPending}
            className="flex items-center gap-1.5 rounded-lg bg-slate-900 px-3 py-1.5 text-[13px] font-medium text-white hover:bg-slate-800 disabled:opacity-50"
          >
            <IconSend size={15} />
            {relay.isPending ? "Repassando…" : "Repassar"}
          </button>
        </div>
      </div>
    </div>
  );
}
